import { AxiosError } from 'axios';
import { PayoutPayload, WithdrawPayload } from '@monorepo/client-types';
import {
  AggregationRequest,
  AirwallexAccountAuth,
  AirwallexBeneficiaryFormElement,
  AirwallexBeneficiaryFormOptions,
  PaginationRequest,
  PaginationResult,
  PaymentMethod,
  Payout,
  PayoutSource,
  PayoutWithProgram,
  RiseInvitationSendResponse,
  WithdrawRulesResponse,
} from '@monorepo/types';
import BaseEntityService, { BaseResponse } from '../core/base-entity-service';

export default class PayoutService extends BaseEntityService {
  static override route = 'payout';

  async getPayouts(
    type: PayoutSource,
    sourceId: number,
    paginateRequest: PaginationRequest,
  ) {
    try {
      const response = await this.httpService.get<
        BaseResponse<PaginationResult<Payout>>
      >(`${this.path}/${type}/${sourceId}`, { params: paginateRequest });

      return response.data;
    } catch (e) {
      console.error(`Failed getting payouts`, e);
      return {
        total: 0,
        results: [],
      };
    }
  }

  async paginatePayouts(paginateRequest: PaginationRequest) {
    try {
      const response = await this.httpService.get<
        BaseResponse<PaginationResult<PayoutWithProgram>>
      >(`${this.path}/paginate`, { params: paginateRequest });
      return response.data;
    } catch (e) {
      console.error(`Failed getting payouts`, e);
      return { total: 0, results: [] };
    }
  }
  async getPayoutsTotal(type: PayoutSource, sourceId: number) {
    try {
      const response = await this.httpService.get<
        BaseResponse<{
          totalAmount: number;
          withdrawalAmount: number;
        }>
      >(`${this.path}/total/${type}/${sourceId}`);

      return response.data;
    } catch (e) {
      console.error(`Failed getting payouts total`, e);
      return {
        totalAmount: 0,
        withdrawalAmount: 0,
      };
    }
  }

  async getPayoutTotalsAndCounts(aggregationRequest: AggregationRequest) {
    try {
      const response = await this.httpService.get<
        BaseResponse<{
          totalAmount: number;
          statusCounts: {
            pending: number;
            completed: number;
            declined: number;
          };
        }>
      >(`${this.path}/totals-and-counts`, { params: aggregationRequest });

      return response.data;
    } catch (e) {
      console.error(`Failed getting payouts totals and counts`, e);
      return {
        totalAmount: 0,
        statusCounts: {
          pending: 0,
          completed: 0,
          declined: 0,
        },
      };
    }
  }

  async getIsEligible(type: PayoutSource, sourceId: string) {
    try {
      const response = await this.httpService.get<
        BaseResponse<WithdrawRulesResponse>
      >(`${this.path}/eligible-withdraw/${type}/${sourceId}`);

      return response.data;
    } catch (e) {
      console.error(`Failed getting payouts`, e);
      return {
        isValid: false,
        availableAmount: 0,
      };
    }
  }

  async createPayout(
    source: PayoutSource,
    amount: number,
    paymentOption: PaymentMethod,
  ) {
    try {
      const response = await this.httpService.post<
        {
          amount: number;
          paymentOption: PaymentMethod;
        },
        BaseResponse<Payout>
      >(`${this.path}/${source}`, { amount, paymentOption });

      return response.data;
    } catch (e) {
      console.error(`Failed creating a payout`, e);
      return {
        total: 0,
        results: [],
      };
    }
  }

  async createPayoutRequest(withdrawRequest: PayoutPayload) {
    const { source, sourceId, ...rest } = withdrawRequest;

    let response;
    try {
      response = await this.httpService.post<
        WithdrawPayload,
        BaseResponse<Payout>
      >(`${this.path}/${source}/${sourceId}`, rest);

      return response.data;
    } catch (e: unknown) {
      if (e instanceof AxiosError) {
        console.error(`Failed creating a payout, ${e.response?.data?.message}`);
        return e.response?.data;
      }
      return e;
    }
  }

  async sendRiseInvitation(): Promise<RiseInvitationSendResponse> {
    try {
      const response = await this.httpService.post<
        string,
        BaseResponse<RiseInvitationSendResponse>
      >(`${this.path}/rise-invitation`);

      return response.data;
    } catch (e) {
      console.error(`Failed sending invitations`, e);
      throw new Error('Failed sending invitations');
    }
  }

  async getRiseId() {
    try {
      const response = await this.httpService.get<
        BaseResponse<{ riseId: string }>
      >(`${this.path}/rise-id`);

      return response.data;
    } catch (e) {
      console.error(`Failed getting rise id`, e);
      return undefined;
    }
  }

  async getAirwallexAuth() {
    try {
      const response = await this.httpService.get<
        BaseResponse<AirwallexAccountAuth>
      >(`${this.path}/airwallex-auth`);
      return response.data;
    } catch (e) {
      console.error(`Failed getting Airwallex auth keys from server`);
      throw e;
    }
  }

  async createAirwallexBeneficiary(
    airwallexForm: AirwallexBeneficiaryFormOptions,
  ) {
    try {
      const response = await this.httpService.post<
        AirwallexBeneficiaryFormOptions,
        BaseResponse<AirwallexBeneficiaryFormElement>
      >(`${this.path}/airwallex-beneficiary`, {
        ...airwallexForm,
      });
      return response.data;
    } catch (e) {
      console.error(`Failed creating Airwallex beneficiary`, e);
      throw e;
    }
  }
}
